export type EquipmentSuggestOption = { value: string; label: string };

type CatalogUnit = {
    categoryId?: string | null;
    manufacturerName?: string | null;
    modelNumber?: string | null;
    engineManufacturer?: string | null;
    engineModelNumber?: string | null;
};

/** When the parent field changes, clear these dependents so models cannot cross makers. */
export const EQUIPMENT_CASCADE_CLEAR_FIELDS: Record<string, string[]> = {
    manufacturerName: ['modelNumber'],
    engineManufacturer: ['engineModelNumber'],
};

export const applyEquipmentFieldCascade = (
    formData: Record<string, unknown>,
    fieldKey: string,
    value: unknown,
): Record<string, unknown> => {
    const next = { ...formData, [fieldKey]: value };
    const clears = EQUIPMENT_CASCADE_CLEAR_FIELDS[fieldKey];
    if (!clears) return next;
    if (formData[fieldKey] === value) return next;
    for (const key of clears) {
        next[key] = '';
    }
    return next;
};

const distinctSortedOptions = (values: Array<string | null | undefined>): EquipmentSuggestOption[] => {
    const names = new Set<string>();
    values.forEach((value) => {
        const trimmed = value?.trim();
        if (trimmed) names.add(trimmed);
    });
    return [...names].sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n }));
};

export const buildEquipmentCatalogAutosuggestOptions = ({
    companyUnits,
    categoryId,
    manufacturerName,
    engineManufacturer,
    engineModelNumber,
}: {
    companyUnits: CatalogUnit[];
    categoryId?: string;
    manufacturerName?: string;
    engineManufacturer?: string;
    engineModelNumber?: string;
}): {
    manufacturerName: EquipmentSuggestOption[];
    modelNumber: EquipmentSuggestOption[];
    engineManufacturer: EquipmentSuggestOption[];
    engineModelNumber: EquipmentSuggestOption[];
} => {
    const categoryUnits = categoryId ? companyUnits.filter((u) => u.categoryId === categoryId) : [];
    const currentManufacturer = manufacturerName?.trim() || '';
    const currentEngineManufacturer = engineManufacturer?.trim() || '';
    const currentEngineModel = engineModelNumber?.trim() || '';

    const manufacturerOptions = distinctSortedOptions(categoryUnits.map((u) => u.manufacturerName));

    const modelNumberOptions = currentManufacturer
        ? distinctSortedOptions(
              categoryUnits
                  .filter((u) => u.manufacturerName === currentManufacturer)
                  .map((u) => u.modelNumber),
          )
        : [];

    const engineManufacturerOptions = distinctSortedOptions([
        ...companyUnits.map((u) => u.engineManufacturer),
        currentEngineManufacturer,
    ]);

    const engineModelNumberOptions = currentEngineManufacturer
        ? distinctSortedOptions([
              ...companyUnits
                  .filter((u) => u.engineManufacturer === currentEngineManufacturer)
                  .map((u) => u.engineModelNumber),
              currentEngineModel,
          ])
        : [];

    return {
        manufacturerName: manufacturerOptions,
        modelNumber: modelNumberOptions,
        engineManufacturer: engineManufacturerOptions,
        engineModelNumber: engineModelNumberOptions,
    };
};
